import { Box, Typography } from '@mui/material'
import type { ReactNode } from 'react'

export type PageHeaderProps = {
  title: ReactNode
  /** One line of context under the title — what this page is for. */
  subtitle?: ReactNode
  /** The page's primary action(s), right-aligned (stack under on phones). */
  actions?: ReactNode
  /** Small line ABOVE the title, e.g. a back link. */
  eyebrow?: ReactNode
  'data-testid'?: string
}

/**
 * The one page-title block for custom (non-List) screens.
 *
 * Before this, every screen invented its own: a 34px bold h4 outside a card
 * (Dashboard), a 34px h4 with an icon INSIDE a card (Pet Shares, Clinic
 * Team), a 34px light h4 with an icon (Help, What's new), an h6 inside the
 * card (Clients, Add New Client, Group Membership Requests). Titles are
 * h5/700 with no leading icon; the page gutter comes from the Layout, so this
 * adds no horizontal padding of its own.
 */
export const PageHeader = ({
  title,
  subtitle,
  actions,
  eyebrow,
  'data-testid': testId,
}: PageHeaderProps) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'flex-start' },
      justifyContent: 'space-between',
      gap: { xs: 1.5, sm: 3 },
      mb: 3,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      {eyebrow && <Box sx={{ mb: 0.5 }}>{eyebrow}</Box>}
      <Typography variant="h5" component="h1" sx={{ lineHeight: 1.3 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions && (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          flexShrink: 0,
          alignItems: 'center',
          // Keep the action optically level with the title's first line.
          pt: { sm: 0.25 },
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
)

export default PageHeader
